import { pmFetchJsonResult } from "@penmate/react-shared/api/pmFetch";
import type { RawMessage } from "@/api/messages";
import { normalizeLetterFromApi } from "@/api/letters";
import {
  RECOVERY_ACCEPT_ERROR_MESSAGE,
  RECOVERY_RESCUE_ERROR_MESSAGE,
  RECOVERY_RESCUE_UNAVAILABLE_MESSAGE,
  recoveryTriggerKind,
} from "@/checkout/recovery";

// Checkout-recovery offer client. Terms and copy are server-owned: the
// client renders exactly what the payload carries and nothing else. Every
// failure path (endpoint absent on an older API, non-2xx, non-JSON, `offer:
// null`, malformed payload) resolves to null / void so checkout is never
// disturbed by this surface. There is no decline call: closing the sheet
// leaves the offer open ("Not now"); the server's follow-up covers non-actors.

export type RecoveryTrigger = "payment_sheet_dismissed" | "checkout_cancel" | "host_back" | "return";

export type RecoveryOfferCopy = {
  headline: string;
  subheadline_lead: string | null;
  subheadline: string | null;
  body: string;
  accept_label: string;
  decline_label: string;
  rescue_label: string | null;
  rescue_detail: string | null;
  fine_print: string | null;
};

export type RecoveryOffer = {
  issuance_id: string;
  archetype: string | null;
  variant_key: string | null;
  price_cents: number;
  max_photos: number;
  expires_at: string | null;
  // Regime D last rung: the server marks an offer whose letter could be
  // rescued free (true-only; absent everywhere else).
  rescue_available: boolean;
  copy: RecoveryOfferCopy;
};

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

const nonNegativeInt = (value: unknown): number | null => {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
};

// A payload missing any term or copy token renders nothing: the sheet has
// no client-side fallback text by design.
export const parseRecoveryOffer = (raw: unknown): RecoveryOffer | null => {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const issuanceId = nonEmptyString(o.issuance_id) ?? (typeof o.issuance_id === "number" ? String(o.issuance_id) : null);
  const priceCents = nonNegativeInt(o.price_cents);
  const maxPhotos = nonNegativeInt(o.max_photos);
  const copyRaw = o.copy && typeof o.copy === "object" ? (o.copy as Record<string, unknown>) : null;
  if (!issuanceId || priceCents == null || maxPhotos == null || !copyRaw) return null;
  const headline = nonEmptyString(copyRaw.headline);
  const body = nonEmptyString(copyRaw.body);
  const acceptLabel = nonEmptyString(copyRaw.accept_label);
  const declineLabel = nonEmptyString(copyRaw.decline_label);
  if (!headline || !body || !acceptLabel || !declineLabel) return null;
  return {
    issuance_id: issuanceId,
    archetype: nonEmptyString(o.archetype),
    variant_key: nonEmptyString(o.variant_key),
    price_cents: priceCents,
    max_photos: maxPhotos,
    expires_at: nonEmptyString(o.expires_at),
    rescue_available: o.rescue_available === true,
    copy: {
      headline,
      subheadline_lead: nonEmptyString(copyRaw.subheadline_lead),
      subheadline: nonEmptyString(copyRaw.subheadline),
      body,
      accept_label: acceptLabel,
      decline_label: declineLabel,
      rescue_label: nonEmptyString(copyRaw.rescue_label),
      rescue_detail: nonEmptyString(copyRaw.rescue_detail),
      fine_print: nonEmptyString(copyRaw.fine_print),
    },
  };
};

const letterPath = (letterId: string, suffix = ""): string =>
  `/letters/${encodeURIComponent(letterId)}/recovery_offer${suffix}`;

export async function requestRecoveryOffer(args: {
  letterId: string;
  trigger: RecoveryTrigger;
}): Promise<RecoveryOffer | null> {
  try {
    const res = await pmFetchJsonResult<{ offer?: unknown }>(letterPath(args.letterId), {
      method: "POST",
      body: { trigger_kind: recoveryTriggerKind(args.trigger), trigger: args.trigger },
    });
    if (!res.ok || !res.data || typeof res.data !== "object") return null;
    return parseRecoveryOffer((res.data as any).offer);
  } catch {
    return null;
  }
}

// Display truth: called from the sheet's render effect, never from the
// trigger response. Best-effort; the server call is idempotent.
export async function markRecoveryOfferDisplayed(args: { letterId: string; issuanceId: string }): Promise<void> {
  try {
    await pmFetchJsonResult(letterPath(args.letterId, "/displayed"), {
      method: "POST",
      body: { issuance_id: args.issuanceId },
    });
  } catch {
    // ignore
  }
}

// Accept is the one call the customer waits on; it is bounded so the sheet
// can never hold them behind the overlay (the trigger is bounded separately).
export const RECOVERY_ACCEPT_TIMEOUT_MS = 10_000;

// The server's error is shown only when it reads as a sentence; bare tokens
// such as "offer_not_available" fall back to the generic line.
const acceptErrorMessage = (data: any): string => {
  const raw =
    (typeof data?.error === "string" && data.error) ||
    (typeof data?.errors === "string" && data.errors) ||
    (Array.isArray(data?.errors) && data.errors.filter((e: unknown) => typeof e === "string").join(" ")) ||
    "";
  const text = raw.trim();
  if (!text || /^[a-z0-9_.-]+$/i.test(text)) return RECOVERY_ACCEPT_ERROR_MESSAGE;
  return text;
};

// Resolves to the re-priced letter (GET /letters/:id shape) when the server
// returns it, or null when it does not; throws when the accept itself failed
// so the sheet can show the server's message and let the user retry.
export async function acceptRecoveryOffer(args: {
  letterId: string;
  issuanceId: string;
  timeoutMs?: number;
}): Promise<RawMessage | null> {
  const request = pmFetchJsonResult<{ letter?: unknown; error?: unknown; errors?: unknown }>(
    letterPath(args.letterId, "/accept"),
    { method: "POST", body: { issuance_id: args.issuanceId } },
  );
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(RECOVERY_ACCEPT_ERROR_MESSAGE)), args.timeoutMs ?? RECOVERY_ACCEPT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([request, timeout]);
    if (!res.ok) throw new Error(acceptErrorMessage(res.data));
    const letter = (res.data as any)?.letter;
    return letter && typeof letter === "object" ? normalizeLetterFromApi(letter) : null;
  } catch (e) {
    if (e instanceof Error) throw e;
    throw new Error(RECOVERY_ACCEPT_ERROR_MESSAGE);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// The server's refusal (422, body `{ error: "rescue_not_available" }` or a
// sentence) is TERMINAL: the rescue was used on another draft, the customer
// is no longer a first-timer, or the draft carries a coupon or an applied
// offer. Nothing a retry can change. Every other failure (the bound, the
// transport, a 5xx) stays retryable.
export class RescueUnavailableError extends Error {
  readonly terminal = true;
  constructor(message: string = RECOVERY_RESCUE_UNAVAILABLE_MESSAGE) {
    super(message);
    this.name = "RescueUnavailableError";
  }
}

export const isRescueUnavailableError = (error: unknown): boolean =>
  error instanceof RescueUnavailableError || (!!error && typeof error === "object" && (error as any).terminal === true);

// Regime D rung 3: grants the free-first rescue. The customer waits on this
// like accept, so it is bounded the same way; a failure throws so the sheet
// can show the message and allow a retry — except the server's refusal,
// which throws the terminal error above. Returns the re-read letter (GET
// shape, meta now reading free) when the server sends it.
export async function redeemFreeRescue(args: { letterId: string; timeoutMs?: number }): Promise<RawMessage | null> {
  const request = pmFetchJsonResult<{ letter?: unknown; error?: unknown; errors?: unknown }>(
    `/letters/${encodeURIComponent(args.letterId)}/free_rescue`,
    { method: "POST", body: {} },
  );
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(RECOVERY_RESCUE_ERROR_MESSAGE)), args.timeoutMs ?? RECOVERY_ACCEPT_TIMEOUT_MS);
  });
  let raisedHere = false;
  try {
    const res = await Promise.race([request, timeout]);
    if (!res.ok) {
      const message = acceptErrorMessage(res.data);
      raisedHere = true;
      if (res.status === 422) {
        throw new RescueUnavailableError(message === RECOVERY_ACCEPT_ERROR_MESSAGE ? RECOVERY_RESCUE_UNAVAILABLE_MESSAGE : message);
      }
      throw new Error(message === RECOVERY_ACCEPT_ERROR_MESSAGE ? RECOVERY_RESCUE_ERROR_MESSAGE : message);
    }
    const letter = (res.data as any)?.letter;
    return letter && typeof letter === "object" ? normalizeLetterFromApi(letter) : null;
  } catch (e) {
    // Only sentences this function raised (incl. the timeout's) reach the
    // sheet; a raw transport failure becomes the generic line.
    if (e instanceof RescueUnavailableError) throw e;
    if (raisedHere && e instanceof Error) throw e;
    if (e instanceof Error && e.message === RECOVERY_RESCUE_ERROR_MESSAGE) throw e;
    throw new Error(RECOVERY_RESCUE_ERROR_MESSAGE);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
